<template>
  <div>
    <v-text-field :class="{'text-body-2':!outlined}"
              :label="label"
              :outlined="outlined" dense
              v-model="formattedValue"
              :disabled="disabled"
              :readonly="readonly"
              :hint="hint"
              :persistent-hint="persistentHint"
              :rules="[rules]"
              @focus="$event.target.select();focused=true"
              @blur="focused=false;format()"
              @input="valueChanged('input')"
              @change="valueChanged('change')"
              :hide-details="hideDetails"
              prefix="" :suffix="suffix"
              :currency="configuration">
      <template v-slot:append v-if="spirit">
        <v-menu :close-on-content-click="false" transition="scale-transition" origin="top right" v-model="menuOpen" offset-y :min-width="width" :max-width="width">
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" :disabled="disabled || readonly" icon small class="mt-n1"><v-icon>mdi-calculator</v-icon></v-btn>
          </template>
          <v-card flat class="pb-2">
            <v-container>
              <v-row dense>
                <v-col cols="10" class="text-h6">Calculate {{strengthOnly?'strength':'LAA'}}</v-col>
                <v-col align="right">
                  <v-btn icon small @click="menuOpen=false"><v-icon>mdi-close</v-icon></v-btn>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" sm="5" v-if="!strengthOnly">
                  <v-radio-group v-model="option" dense>
                    <v-radio label="From density/weight" value="A"/>
                    <v-radio label="From density/volume" value="B"/>
                    <v-radio label="From ABV/weight" value="C"/>
                    <v-radio label="From ABV/volume" value="D"/>
                    <v-radio label="From ABW/weight" value="N"/>
                  </v-radio-group>
                </v-col>
                <v-col :cols="strengthOnly ? 12 : 7">
                  <v-form v-model="valid">
                    <v-container class="pb-0">
                      <v-row dense>
                        <v-col cols="6">
                          <decimal-number-input label="Temperature" v-model="temp" prefix="" suffix="°C" :precision="1" v-if="option!=='N'"
                                                hint="0.5 increments" :min="-20" :max="40" :rules="[halfDegree]"/>
                        </v-col>
                        <v-col cols="6">
                          <decimal-number-input v-if="'AB'.includes(option)"
                                                label="Density" v-model="density" prefix="" suffix="kg/m³" :precision="1"
                                                hint="0.2 increments" :min="780" :max="999.8" :rules="[point2]"/>
                          <decimal-number-input v-else-if="'CD'.includes(option)"
                                                label="ABV" v-model="sstrength"
                                                prefix="" suffix="% ABV" :precision="2"/>
                          <decimal-number-input v-else-if="option==='N'"
                                                label="ABW" v-model="sstrength"
                                                prefix="" suffix="% ABW" :precision="2"/>
                        </v-col>
                      </v-row>
                      <v-row dense v-if="value != undefined">
                        <v-col cols="6">
                          <decimal-number-input v-if="'ACN'.includes(option)"
                                                label="Weight" v-model="weight" prefix="" suffix="kg" :precision="3"/>
                          <decimal-number-input v-else
                                                label="Volume" v-model="volume" prefix="" suffix="litres" :precision="3"/>
                        </v-col>
                        <v-col cols="6">
                          <decimal-number-input v-if="'ACN'.includes(option)"
                                                label="Tare" v-model="wtare" prefix="" suffix="kg " :precision="3"/>
                        </v-col>
                      </v-row>
                      <v-row >
                        <v-col align="center">
                          <v-btn color="primary" @click="calculate" :loading="loading">Calculate</v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-menu>
      </template>
    </v-text-field>

    <template v-if="spirit && !strengthOnly">
      <decimal-number-input v-if="trackLitres" :class="{'text-body-2':!outlined}" label="Volume"
                            suffix="litres" :precision="3" prefix="" :outlined="outlined" v-model="llitres"
                            :disabled="disabled" :readonly="readonly" :hide-details="hideDetails"
                            @input="$emit('update:litres', llitres)"/>
      <template v-if="!dontShowStrengthAndKg">
        <decimal-number-input v-if="trackKg" :class="{'text-body-2':!outlined}" label="Weight"
                              suffix="kg" :precision="3" prefix="" :outlined="outlined" v-model="kkg"
                              :disabled="disabled" :readonly="readonly" :hide-details="hideDetails"
                              @input="$emit('update:kg', kkg)"/>
        <decimal-number-input v-if="trackStrength" :class="{'text-body-2':!outlined}" label="Strength"
                              suffix="% alc./vol." :precision="2" prefix="" :outlined="outlined" v-model="sstrength"
                              :disabled="disabled" :readonly="readonly" :hide-details="hideDetails"
                              @input="$emit('update:strength', sstrength)"/>
      </template>
    </template>
  </div>
</template>

<script>
import { CurrencyDirective, parse } from 'vue-currency-input'

export default {
  name: 'AlcoholInput',
  directives: {currency: CurrencyDirective},
  props: {
    value: {type:Number,required:false},
    label: {type:String, required:false},
    spirit: {type:Boolean, required:false, default:true},
    strength: {type:Number,required:false, default:0},
    strengthOnly: {type:Boolean, required:false, default:false},
    tare: {type:Number,required:false,default:0},
    litres: {type:Number,required:false, default:0},
    kg: {type:Number,required:false, default:0},
    dontShowStrengthAndKg: {type:Boolean,required:false, default:false},    // Used for cask fill

    disabled: {type:Boolean, required:false, default:false},
    readonly: {type:Boolean, required:false, default:false},
    hint: {type:String, required:false},
    persistentHint: {type:Boolean, required:false},
    outlined: {type:Boolean,required:false, default:true},
    hideDetails: {type:Boolean,required:false}
  },
  data() {
    return {
      focused: false,
      formattedValue: this.value,
      menuOpen: false,
      option: 'A',
      valid: true,

      temp: 20,
      density: 900,
      weight: 0,
      wtare: this.tare,
      volume: 0,
      sstrength: this.strength,
      llitres: this.litres,
      kkg: this.kg,

      halfDegree: v => v % .5 === 0 || '0.5 increments',
      point2: v => v*10 % 2 === 0 || '0.2 increments',
      loading: false
    }
  },
  watch: {
    value() {
      if (!this.focused)
        this.format();
    },
    spirit() {
      if (!this.focused)
        this.format();
    },
    strength(newValue) {
      if (this.strengthOnly && !this.focused)
        this.formattedValue = this.$number(newValue,2);
      this.sstrength = this.strength;
    },
    litres() {
      this.llitres = this.litres;
    },
    kg() {
      this.kkg = this.kg;
    },
    tare() {
      this.wtare = this.tare;
    }
  },
  computed: {
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    trackKg() {
      return this.$store.getters.company.settings.trackKg;
    },
    trackStrength() {
      return this.$store.getters.company.settings.trackStrength;
    },
    width() {
      return this.strengthOnly ? '290px' : '550px';
    },
    configuration() {
      const configuration = {precision:this.strengthOnly ? 2 : 3, currency: {prefix:''}};
      return configuration;
    },
    suffix() {
      if (this.strengthOnly)
        return '% alc./vol.';
     return this.spirit ? 'LAA' : 'litres';
    }
  },
  created() {
    this.format();
  },
  methods: {
    format() {
      this.formattedValue = this.$number(this.strengthOnly ? this.strength : this.value, this.strengthOnly ? 2 : 3);
    },
    rules(v) {
      if (this.strengthOnly && (v < 0 || v > 99))
        return `Strength must be between 0% and 99%`;
      if (v < 0)
        return `Must be > 0`;
      return true;
    },
    valueChanged(event) {
      let value = 0;
      if (this.formattedValue != '')
        value = parse((this.formattedValue.startsWith('.') ? '0' : '') + this.formattedValue, this.configuration) || 0;
      else
        value = 0;
      this.$emit(event, this.$roundNumber(value, 3));
      if (this.strengthOnly)
        this.$emit('update:strength', this.$roundNumber(value,2));
    },
    async lookupByDensity() {
      const results = await this.$apiCall(`query Calculator($temp: Float!, $density: Float!) {
                                              alcoholometricLookupByDensity(temp:$temp, density:$density) {temp,density,a,b,c}
                                            }`,
                                          {temp:this.temp,density:this.density});
      if (!results.error)
        return results.data.alcoholometricLookupByDensity;
    },
    async lookupByStrength(strength) {
      const results = await this.$apiCall(`query Calculator($temp: Float!, $b: Float!) {
                                              alcoholometricLookupByStrength(temp:$temp, b:$b) {temp,density,a,b,c}
                                            }`,
                                          {temp:this.temp,b:strength});
      if (!results.error)
        return results.data.alcoholometricLookupByStrength;
    },
    async calculate() {
      if (!this.valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.loading = true;
      const lookup = await ('AB'.includes(this.option) ? this.lookupByDensity() : this.lookupByStrength(this.option === 'N' ? 100 : this.sstrength));
      this.loading = false;
      if (!lookup)
        return;

      if (this.strength != undefined) {
        if (this.strengthOnly)
          this.formattedValue = this.$number(lookup.b/100,2);
        this.$emit('update:strength', lookup.b);
      }
      if (this.value != undefined) {
        let laa = 0;
        if (this.option === 'A' || this.option === 'C')
          laa = (this.weight - this.wtare) * lookup.a * lookup.b / 100;
        else if (this.option === 'B' || this.option === 'D')
          laa = this.volume * lookup.c * lookup.b / 100;
        else if (this.option === 'N')
          laa = (this.weight - this.wtare) * (this.sstrength/100) / 0.78924;
        this.$emit('input', this.$roundNumber(laa,3));
        this.$emit('change', this.$roundNumber(laa,3));

        if (this.trackLitres) {
          if (this.option === 'B' || this.option === 'D')
            this.$emit('update:litres', this.volume);
          else
            this.$emit('update:litres', this.$roundNumber(laa/(lookup.b/100), 3));
        }
        if (this.trackKg) {
          if (this.option === 'A' || this.option === 'C' || this.option === 'E')
            this.$emit('update:kg', this.weight);
          else
            this.$emit('update:kg', this.$roundNumber(this.volume*lookup.c / lookup.a, 3));
        }
        if (this.trackStrength) {
          if (this.option === 'C' || this.option === 'D')
            this.$emit('update:strength', this.strength);
          else
            this.$emit('update:strength', this.$roundNumber(lookup.b, 2));
        }
      }
      this.menuOpen = false;
    }
  }
}

</script>